import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectContent, SelectValue, SelectItem } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';

export default function DesignSystem() {
  const { success, error, info, warning } = useToast();

  return (
    <div className="min-h-screen bg-linen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="CareCal" className="h-20 w-auto" />
          </div>
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-4">Design System</h2>
          <p className="font-body text-xl text-night-900">
            A comprehensive showcase of components, colors, and typography
          </p>
        </div>

        {/* Color Palette */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Color Palette</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Blush Colors */}
            <Card>
              <CardHeader>
                <CardTitle>Lilac & Blueviolet (Primary)</CardTitle>
                <CardDescription>Main brand color palette</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-lilac-100 border border-night-100" />
                  <div>
                    <p className="font-body font-semibold">lilac-100</p>
                    <p className="text-sm text-night-500">#FCFBFF</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-lilac-500 border border-night-100" />
                  <div>
                    <p className="font-body font-semibold">lilac-500</p>
                    <p className="text-sm text-night-500">#F6F3FF</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-lilac-900" />
                  <div>
                    <p className="font-body font-semibold">lilac-900</p>
                    <p className="text-sm text-night-500">#E6DDFF</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-blueviolet-light" />
                  <div>
                    <p className="font-body font-semibold ">blueviolet-light</p>
                    <p className="text-sm text-night-500">#7175C9</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-blueviolet" />
                  <div>
                    <p className="font-body font-semibold ">blueviolet</p>
                    <p className="text-sm text-night-500">#5054BC</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-blueviolet-dark" />
                  <div>
                    <p className="font-body font-semibold ">blueviolet-dark</p>
                    <p className="text-sm text-night-500">#424395</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Accent Colors */}
            <Card>
              <CardHeader>
                <CardTitle>Accent Colors</CardTitle>
                <CardDescription>Supporting brand colors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-linen border border-night-100" />
                  <div>
                    <p className="font-body font-semibold">linen</p>
                    <p className="text-sm text-night-500">#FFFEF9</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-tigerlily" />
                  <div>
                    <p className="font-body font-semibold ">tigerlily</p>
                    <p className="text-sm text-night-500">#F8584C</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-avocado border border-night-100" />
                  <div>
                    <p className="font-body font-semibold ">avocado</p>
                    <p className="text-sm text-night-500">#EDF8B0</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Gray Scale */}
            <Card>
              <CardHeader>
                <CardTitle>Night</CardTitle>
                <CardDescription>Neutral colors for text and UI</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-night-100 border border-night-300" />
                  <div>
                    <p className="font-body font-semibold">night-100</p>
                    <p className="text-sm text-night-500">#E3E1E4</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-night-300" />
                  <div>
                    <p className="font-body font-semibold">night-300</p>
                    <p className="text-sm text-night-500">#C6C3C8</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-night-500" />
                  <div>
                    <p className="font-body font-semibold">night-500</p>
                    <p className="text-sm text-night-500">#8C8690</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-night-700" />
                  <div>
                    <p className="font-body font-semibold ">night-700</p>
                    <p className="text-sm text-night-500">#524A58</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-lg bg-night-900" />
                  <div>
                    <p className="font-body font-semibold ">night-900</p>
                    <p className="text-sm text-night-500">#180D20</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Typography */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Typography</h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Headings (Epilogue)</CardTitle>
                <CardDescription>Display font for titles and headings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h1 className="font-display text-4xl font-bold text-blueviolet">
                    Heading 1 - 32px
                  </h1>
                  <p className="text-sm text-night-500 mt-1">font-display text-4xl font-bold</p>
                </div>
                <div>
                  <h2 className="font-display text-3xl font-bold text-blueviolet">
                    Heading 2 - 24px
                  </h2>
                  <p className="text-sm text-night-500 mt-1">font-display text-3xl font-bold</p>
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-blueviolet">
                    Heading 3 - 20px
                  </h3>
                  <p className="text-sm text-night-500 mt-1">font-display text-2xl font-bold</p>
                </div>
                <div>
                  <h4 className="font-display text-xl font-semibold text-blueviolet">
                    Heading 4 - 18px
                  </h4>
                  <p className="text-sm text-night-500 mt-1">font-display text-xl font-semibold</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Body Text (Nunito)</CardTitle>
                <CardDescription>Body font for content and UI</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-body text-lg text-night-900">
                    Body Large - 16px / 24px line height
                  </p>
                  <p className="text-sm text-night-500 mt-1">font-body text-lg</p>
                </div>
                <div>
                  <p className="font-body text-base text-night-900">
                    Body Regular - 14px / 21px line height
                  </p>
                  <p className="text-sm text-night-500 mt-1">font-body text-base</p>
                </div>
                <div>
                  <p className="font-body text-sm text-night-900">
                    Body Small - 12px / 18px line height
                  </p>
                  <p className="text-sm text-night-500 mt-1">font-body text-sm</p>
                </div>
                <div>
                  <p className="font-body text-xs font-medium text-night-900 uppercase">
                    Caption - 11px / 16px
                  </p>
                  <p className="text-sm text-night-500 mt-1">
                    font-body text-xs font-medium uppercase
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Buttons */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Buttons</h2>

          <Card>
            <CardHeader>
              <CardTitle>Button Variants</CardTitle>
              <CardDescription>Different button styles for various use cases</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <Button>Default Button</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="destructive">Destructive</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="link">Link</Button>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Button size="sm">Small</Button>
                <Button size="default">Default</Button>
                <Button size="lg">Large</Button>
                <Button size="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </Button>
              </div>

              <div className="mt-6 flex flex-wrap gap-4">
                <Button disabled>Disabled</Button>
                <Button variant="secondary" disabled>
                  Disabled Secondary
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Badges */}
        <section className="mb-16">
        <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Badges</h2>
          <Card>
            <CardHeader>
              <CardTitle>Badges</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge variant="default">Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="success">Success</Badge>
                <Badge variant="warning">Warning</Badge>
                <Badge variant="destructive">Error</Badge>
                <Badge variant="outline">Outline</Badge>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Booking Status    Badges:</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="pending">Pending</Badge>
                  <Badge variant="accepted">Accepted</Badge>
                  <Badge variant="declined">Declined</Badge>
                  <Badge variant="confirmed">Confirmed</Badge>
                  <Badge variant="completed">Completed</Badge>
                  <Badge variant="canceled">Canceled</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Form Inputs */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Form Inputs</h2>

          <Card>
            <CardHeader>
              <CardTitle>Input Fields</CardTitle>
              <CardDescription>Text inputs and form controls</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="font-body text-sm font-medium text-night-700 mb-2 block">
                  Default Input
                </label>
                <Input placeholder="Enter text here..." />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-night-700 mb-2 block">
                  Email Input
                </label>
                <Input type="email" placeholder="email@example.com" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Textarea</label>
                <Textarea placeholder="Tell us about yourself" />
              </div>
              <div>
                <label className="font-body text-sm font-medium text-night-700 mb-2 block">
                  Disabled Input
                </label>
                <Input disabled placeholder="Disabled input" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-night-700">Select</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="option1">Option 1</SelectItem>
                    <SelectItem value="option2">Option 2</SelectItem>
                    <SelectItem value="option3">Option 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="terms" />
                <label
                  htmlFor="terms"
                  className="text-sm font-medium leading-none     peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Accept terms and conditions
                </label>
              </div>
              
            </CardContent>
          </Card>
        </section>

        {/* Cards */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Cards</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Simple Card</CardTitle>
                <CardDescription>A basic card with title and description</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="font-body text-night-500">
                  Cards are versatile containers for grouping related content and actions.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-lilac-100 border-lilac-900">
              <CardHeader>
                <CardTitle className="text-blueviolet">Styled Card</CardTitle>
                <CardDescription>Custom background and border colors</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="font-body text-night-500">
                  Cards can be customized with different colors and styles.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle>Interactive Card</CardTitle>
                <CardDescription>With hover effects</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="font-body text-night-500">
                  Hover over this card to see the shadow effect.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Dialogs */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Dialogs</h2>

          <Card>
            <CardHeader>
              <CardTitle>Modal Dialogs</CardTitle>
              <CardDescription>Overlay dialogs for focused interactions</CardDescription>
            </CardHeader>
            <CardContent>
              <Dialog>
                <DialogTrigger asChild>
                  <Button>Open Dialog</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Example Dialog</DialogTitle>
                    <DialogDescription>
                      This is a modal dialog built with Radix UI primitives and styled with Tailwind
                      CSS.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="py-4">
                    <p className="font-body text-night-500">
                      Dialogs are perfect for confirmations, forms, or any content that needs user
                      focus.
                    </p>
                  </div>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </section>

        {/* Toasts */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">
            Toast Notifications
          </h2>

          <Card>
            <CardHeader>
              <CardTitle>Toast Messages</CardTitle>
              <CardDescription>
                Temporary notifications with auto-dismiss after 4 seconds. Positioned top-right on
                desktop, top-center on mobile.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-body font-semibold text-night-900 mb-3">Basic Variants</h3>
                <div className="flex flex-wrap gap-4">
                  <Button onClick={() => success('Success! Operation completed.')}>
                    Success Toast
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => error('Error! Something went wrong.')}
                  >
                    Error Toast
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => info('Info: Here is some information.')}
                  >
                    Info Toast
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => warning('Warning: Please review your input.')}
                  >
                    Warning Toast
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="font-body font-semibold text-night-900 mb-3">With Description</h3>
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() =>
                      success('Profile updated', {
                        description: 'Your changes have been saved successfully.',
                      })
                    }
                  >
                    With Description
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() =>
                      error('Failed to save', {
                        description: 'Please check your internet connection and try again.',
                      })
                    }
                  >
                    Error with Details
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="font-body font-semibold text-night-900 mb-3">With Action Button</h3>
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={() =>
                      success('Booking request sent', {
                        description: 'The caregiver will respond soon.',
                        action: {
                          label: 'View',
                          onClick: () => alert('View clicked!'),
                        },
                      })
                    }
                  >
                    With Action
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      info('New message received', {
                        action: {
                          label: 'Read',
                          onClick: () => alert('Read clicked!'),
                        },
                      })
                    }
                  >
                    Info with Action
                  </Button>
                </div>
              </div>

              <div>
                <h3 className="font-body font-semibold text-night-900 mb-3">Custom Duration</h3>
                <div className="flex flex-wrap gap-4">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      info('This toast stays for 10 seconds', {
                        duration: 10000,
                      })
                    }
                  >
                    Long Duration (10s)
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      warning('Quick message', {
                        duration: 2000,
                      })
                    }
                  >
                    Short Duration (2s)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Spacing */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Spacing Scale</h2>

          <Card>
            <CardHeader>
              <CardTitle>Spacing Tokens</CardTitle>
              <CardDescription>Consistent spacing throughout the application</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-xs h-8 bg-blueviolet rounded" />
                <span className="font-body">xs - 4px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-sm h-8 bg-blueviolet rounded" />
                <span className="font-body">sm - 8px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-md h-8 bg-blueviolet rounded" />
                <span className="font-body">md - 12px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-base h-8 bg-blueviolet rounded" />
                <span className="font-body">base - 16px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-lg h-8 bg-blueviolet rounded" />
                <span className="font-body">lg - 24px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-xl h-8 bg-blueviolet rounded" />
                <span className="font-body">xl - 32px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-2xl h-8 bg-blueviolet rounded" />
                <span className="font-body">2xl - 48px</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-3xl h-8 bg-blueviolet rounded" />
                <span className="font-body">3xl - 64px</span>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Shadows */}
        <section className="mb-16">
          <h2 className="font-display text-3xl font-bold text-blueviolet mb-6">Shadows</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="shadow-xs">
              <CardContent className="pt-6">
                <p className="font-body font-semibold mb-2">shadow-xs</p>
                <p className="text-sm text-night-500">Extra small shadow</p>
              </CardContent>
            </Card>
            <Card className="shadow-sm">
              <CardContent className="pt-6">
                <p className="font-body font-semibold mb-2">shadow-sm</p>
                <p className="text-sm text-night-500">Small shadow</p>
              </CardContent>
            </Card>
            <Card className="shadow-md">
              <CardContent className="pt-6">
                <p className="font-body font-semibold mb-2">shadow-md</p>
                <p className="text-sm text-night-500">Medium shadow</p>
              </CardContent>
            </Card>
            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <p className="font-body font-semibold mb-2">shadow-lg</p>
                <p className="text-sm text-night-500">Large shadow</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center py-8 border-t border-night-100">
          <p className="font-body text-night-500">
            CareCal Design System • Built with React, TypeScript, Tailwind CSS, and shadcn/ui
          </p>
        </div>
      </div>
    </div>
  );
}
